import { useState, useCallback, useEffect, useMemo } from "react";
import DxfParser from "dxf-parser";
import PredefinedPoints from "./PredefinedPoints";
import Result from "./shap/Result";
import CommonInput from "./shap/Commonnput";
import { COM } from "./AdvanceOutput/COM";
import { ComputeMomentOfInertia } from "./AdvanceOutput/MomentOfInertia";
import { ComputePrincipalAxisAngle } from "./AdvanceOutput/PrincipalAxisAngle";
import LineAtTheta from "./Graph/Shap/LineAtθ";
import CircleSector from "./Graph/Shap/Circle";
import Feasibility from "./Feasibility";
import FeasibilityL1 from "./FeasibilityL1";
import "../App.css";

const TWO_PI = 2 * Math.PI;

function normalizeAngle(angle) {
  return ((angle % TWO_PI) + TWO_PI) % TWO_PI;
}

function arcSweep(startAngle, endAngle, clockwise) {
  if (clockwise) {
    return normalizeAngle(startAngle - endAngle);
  }
  return normalizeAngle(endAngle - startAngle);
}

function arcRenderRotation(shape) {
  return (shape.clockwise ? shape.endAngle : shape.startAngle) * 180 / Math.PI;
}

function lineAngle(p1, p2) {
  return Math.atan2(p2.y - p1.y, p2.x - p1.x);
}

function bulgeToArc(x1, y1, x2, y2, bulge) {
  const d = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);

  if (d <= 1e-9 || Math.abs(bulge) < 1e-9) {
    return {
      type: "LINE",
      start: { x: x1, y: y1 },
      end: { x: x2, y: y2 },
      angle: lineAngle({ x: x1, y: y1 }, { x: x2, y: y2 }),
    };
  }

  const theta = 4 * Math.atan(Math.abs(bulge));
  const radius = d / (2 * Math.sin(theta / 2));
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  const px = -dy / len;
  const py = dx / len;
  const h = radius * Math.cos(theta / 2);
  const sign = bulge > 0 ? 1 : -1;
  const cx = mx + sign * h * px;
  const cy = my + sign * h * py;
  const startAngle = Math.atan2(y1 - cy, x1 - cx);
  const endAngle = Math.atan2(y2 - cy, x2 - cx);

  return {
    type: "ARC",
    center: { x: cx, y: cy },
    radius,
    startAngle,
    endAngle,
    clockwise: bulge < 0,
  };
}

function lwpolylineToSegments(vertices, closed) {
  const n = vertices.length;
  const segments = [];
  const limit = closed ? n : n - 1;

  for (let i = 0; i < limit; i++) {
    const p1 = vertices[i];
    const p2 = vertices[(i + 1) % n];
    const bulge = p1.bulge ?? 0;

    if (Math.abs(bulge) < 1e-9) {
      segments.push({
        type: "LINE",
        start: { x: p1.x, y: p1.y },
        end: { x: p2.x, y: p2.y },
        angle: lineAngle(p1, p2),
      });
    } else {
      segments.push(bulgeToArc(p1.x, p1.y, p2.x, p2.y, bulge));
    }
  }

  return segments;
}

function segmentMidpoint(segment) {
  if (segment.type === "ARC") {
    return segment.center;
  }
  return {
    x: (segment.start.x + segment.end.x) / 2,
    y: (segment.start.y + segment.end.y) / 2,
  };
}

function reverseSegment(segment) {
  if (segment.type === "LINE") {
    return {
      ...segment,
      start: segment.end,
      end: segment.start,
      angle: lineAngle(segment.end, segment.start),
    };
  }
  return {
    ...segment,
    startAngle: segment.endAngle,
    endAngle: segment.startAngle,
    clockwise: !segment.clockwise,
  };
}

function reversePolyline(polyline) {
  return [...polyline].reverse().map(reverseSegment);
}

function alignPolylines(polyA, polyB) {
  if (polyA.length !== polyB.length) {
    return [polyA, polyB];
  }

  const dist2 = (a, b) => (a.x - b.x) ** 2 + (a.y - b.y) ** 2;

  const scoreAlignment = (candidateB, offset) => {
    let score = 0;
    for (let i = 0; i < polyA.length; i++) {
      const a = polyA[i];
      const b = candidateB[(i + offset) % candidateB.length];
      if (a.type !== b.type) {
        score += 1e9;
        continue;
      }
      if (a.type === "ARC") {
        score += dist2(a.center, b.center);
      } else {
        score += dist2(segmentMidpoint(a), segmentMidpoint(b));
      }
    }
    return score;
  };

  let best = { score: Infinity, offset: 0, polyline: polyB };

  for (const candidate of [polyB, reversePolyline(polyB)]) {
    for (let offset = 0; offset < candidate.length; offset++) {
      const score = scoreAlignment(candidate, offset);
      if (score < best.score) {
        best = { score, offset, polyline: candidate };
      }
    }
  }

  const rotatedB = [
    ...best.polyline.slice(best.offset),
    ...best.polyline.slice(0, best.offset),
  ];

  return [polyA, rotatedB];
}

function makeMidlineFromAlignedPolylines(polyA, polyB) {
  const n = Math.min(polyA.length, polyB.length);
  const midline = [];
  const thicknessValues = [];
  const dist = (a, b) => Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);

  for (let i = 0; i < n; i++) {
    const a = polyA[i];
    const b = polyB[i];

    if (a.type === "LINE" && b.type === "LINE") {
      const directDist = dist(a.start, b.start) + dist(a.end, b.end);
      const reverseDist = dist(a.start, b.end) + dist(a.end, b.start);
      const bStart = reverseDist < directDist ? b.end : b.start;
      const bEnd = reverseDist < directDist ? b.start : b.end;

      midline.push({
        type: "LINE",
        start: {
          x: (a.start.x + bStart.x) / 2,
          y: (a.start.y + bStart.y) / 2,
        },
        end: {
          x: (a.end.x + bEnd.x) / 2,
          y: (a.end.y + bEnd.y) / 2,
        },
        angle: a.angle,
      });
      thicknessValues.push((dist(a.start, bStart) + dist(a.end, bEnd)) / 2);
    } else if (a.type === "ARC" && b.type === "ARC") {
      midline.push({
        type: "ARC",
        center: {
          x: (a.center.x + b.center.x) / 2,
          y: (a.center.y + b.center.y) / 2,
        },
        radius: (a.radius + b.radius) / 2,
        startAngle: a.startAngle,
        endAngle: a.endAngle,
        clockwise: a.clockwise,
      });
      thicknessValues.push(Math.abs(a.radius - b.radius));
    }
  }

  const valid = thicknessValues.filter((v) => Number.isFinite(v) && v > 0);
  const averageThickness =
    valid.length > 0
      ? valid.reduce((s, v) => s + v, 0) / valid.length
      : 1;

  return { midline, averageThickness };
}

export default function FromDxf({ materials }) {
  const aa = 180 / Math.PI;
  const shapeData = [];

  const [selectedShapeData, setSelectedShapeData] = useState([]);
  const [boxPerimeter, setBoxPerimeter] = useState(0);
  const [entities, setEntities] = useState([]);
  const [predefinedPoints, setPredefinedPoints] = useState([]);
  const [dimensioning, setDimensioning] = useState(false);
  const [showGrid, setShowGrid] = useState(true); // ← grid toggle state
  let clockwise = true;
  const [clockWises, setClockWises] = useState(true);
  let mnx = 0;
  let mny = 0;
  let mxx = 40;
  let mxy = 40;
  const [startX, setStartX] = useState(30);
  const [startY, setStartY] = useState(30);
  const [endX, setEndX] = useState(30);
  const [endY, setEndY] = useState(30);
  const [thickness, setThickness] = useState(-1);
  const [innerThickness, setInnerThickness] = useState(-2);
  const [outerThickness, setOuterThickness] = useState(-3);
  const [thicknessIncreaseSide, setThicknessIncreaseSide] = useState("Inner");
  const [viewBox, setViewBox] = useState("0 0 40 40");
  const [isDragging, setIsDragging] = useState(false);
  const [startCoords, setStartCoords] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [stripWidth, setStripWidth] = useState(0);
  const [weightPerLength, setWeightPerLength] = useState(0);
  const [length, setLength] = useState(1);
  const [density, setDensity] = useState(7850);
  const [totalWeight, setTotalWeight] = useState(0);
  const [outLine, setOutLine] = useState(0);
  const [area, setArea] = useState(0);
  const [inertiax, setInertiax] = useState(0);
  const [inertiay, setInertiay] = useState(0);
  const [rogx, setRogx] = useState(0);
  const [rogy, setRogy] = useState(0);
  const [pmoi, setPmoi] = useState(0);
  const [type, setType] = useState("Close");
  const [morx, setMorx] = useState(0);
  const [mory, setMory] = useState(0);
  const [inertiaxy, setInertiaxy] = useState(0);
  const [paangle, setPaangle] = useState(0);
  const [inertiau, setInertiau] = useState(0);
  const [inertiav, setInertiav] = useState(0);
  const [rogu, setRogu] = useState(0);
  const [rogv, setRogv] = useState(0);
  const [moru, setMoru] = useState(0);
  const [morv, setMorv] = useState(0);
  let a;
  let b;
  let Ix;
  let Iy;
  let sw;
  let ol;
  let acs;
  let xmax;
  let ymax;
  let Ixy;
  const mx = 100;
  const ratio = 100;

  // ─── Dynamic grid size: always ~10 cells across the current viewBox width ───
  const gridSize = useMemo(() => {
    const parts = viewBox.split(" ").map(Number);
    const vw = parts[2];
    if (!vw || vw <= 0) return 10;
    const raw = vw / 10;
    const exp = Math.pow(10, Math.floor(Math.log10(raw)));
    const rounded = Math.ceil(raw / exp) * exp;
    return rounded > 0 ? rounded : 10;
  }, [viewBox]);

  const handleThicknessChange = (e) => {
    if (thicknessIncreaseSide === "Inner") {
      setInnerThickness(Number(e.target.value));
    } else {
      setOuterThickness(Number(e.target.value));
    }
  };

  const clickOndimensioning = () => setDimensioning(!dimensioning);

  const handleUpload = async (e) => {
    setThickness(-1);
    setDimensioning(false);
    const file = e.target.files[0];
    if (!file) return;
    const text = await file.text();
    const parser = new DxfParser();
    const dxf = parser.parseSync(text);
    setEntities(dxf.entities || []);
  };

  useEffect(() => {
    setSelectedShapeData([]);
    shapeData.length = 0;
    let LWPOLYLINE = false;
    let thickness1 = 0;
    let newShape = "Close";
    const lwPolylines = [];

    if (entities.length > 0) {
      mnx = Number.MAX_SAFE_INTEGER;
      mny = Number.MAX_SAFE_INTEGER;
      mxx = Number.MIN_SAFE_INTEGER;
      mxy = Number.MIN_SAFE_INTEGER;
    }

    for (let i = 0; i < entities.length; i++) {
      const ent = entities[i];

      if (ent.type === "LINE") {
        const p1 = ent.vertices[0];
        const p2 = ent.vertices[1];
        if (ent.layer === "CIRCLES") setType("Open");
        const angle1 = lineAngle(p1, p2);
        thickness1 = ent.lineweight / 100;
        mnx = Math.min(mnx, p1.x, p2.x);
        mny = Math.min(mny, p1.y, p2.y);
        mxx = Math.max(mxx, p1.x, p2.x);
        mxy = Math.max(mxy, p1.y, p2.y);
        shapeData.push({
          type: "LINE",
          start: { x: p1.x, y: p1.y },
          end: { x: p2.x, y: p2.y },
          angle: angle1,
        });
      }

      if (ent.type === "ARC") {
        const p = ent.center;
        if (ent.layer === "CIRCLES") setType("Open");
        thickness1 = ent.lineweight / 100;
        mnx = Math.min(mnx, p.x - ent.radius);
        mny = Math.min(mny, p.y - ent.radius);
        mxx = Math.max(mxx, p.x + ent.radius);
        mxy = Math.max(mxy, p.y + ent.radius);
        shapeData.push({
          type: "ARC",
          center: { x: p.x, y: p.y },
          radius: ent.radius,
          clockwise: ent.endAngle > ent.startAngle ? false : true,
          endAngle: ent.endAngle,
          startAngle: ent.startAngle,
        });
      }

      if (ent.type === "LWPOLYLINE") {
        LWPOLYLINE = true;

        const verts = ent.vertices || [];
        for (const v of verts) {
          mnx = Math.min(mnx, v.x);
          mny = Math.min(mny, v.y);
          mxx = Math.max(mxx, v.x);
          mxy = Math.max(mxy, v.y);
        }

        if (ent.shape === false) {
          setType("Open");
          newShape = "Open";
        }

        const segments = lwpolylineToSegments(verts, ent.shape !== false);
        lwPolylines.push(segments);
      }
    }

    setStartX(mnx);
    setStartY(mny);
    setEndX(mxx);
    setEndY(mxy);

    if (LWPOLYLINE && lwPolylines.length >= 2) {
      shapeData.length = 0;
      thickness1 = 0;

      const [alignedA, alignedB] = alignPolylines(lwPolylines[0], lwPolylines[1]);
      const { midline, averageThickness } = makeMidlineFromAlignedPolylines(alignedA, alignedB);

      shapeData.push(...midline);
      thickness1 = averageThickness;
      setType(newShape);
    } else if (LWPOLYLINE && lwPolylines.length === 1) {
      shapeData.length = 0;
      thickness1 = 0;
      const dxfData = lwPolylines[0];
      const n = dxfData.length;
      let s0 = 0;

      for (let i = 1; i <= n; i++) {
        if (
          dxfData[i % n].type === "LINE" &&
          dxfData[(i - 1) % n].type === "LINE" &&
          dxfData[(i + 1) % n].type === "LINE"
        ) {
          if (
            normalizeAngle(dxfData[(i - 1) % n].angle) ===
            normalizeAngle(Math.PI + dxfData[(i + 1) % n].angle)
          ) {
            s0 = (i + 1) % n;
            break;
          }
        }

        if (
          dxfData[i % n].type === "LINE" &&
          dxfData[(i - 1) % n].type === "ARC" &&
          dxfData[(i + 1) % n].type === "ARC"
        ) {
          if (
            dxfData[(i - 1) % n].center.x === dxfData[(i + 1) % n].center.x &&
            dxfData[(i - 1) % n].center.y === dxfData[(i + 1) % n].center.y
          ) {
            s0 = (i + 1) % n;
            break;
          }
        }
        setType("Open");
      }

      if (dxfData[s0] && dxfData[s0].type === "LINE") {
        thickness1 = Math.sqrt(
          Math.pow(dxfData[s0].end.x - dxfData[s0].start.x, 2) +
          Math.pow(dxfData[s0].end.y - dxfData[s0].start.y, 2)
        );
      }

      const s1 = s0 + n;
      const s2 = s0 + n - 2;

      for (let i = 0; i < Math.floor(dxfData.length / 2) - 1; i++) {
        const first = (s1 + i) % n;
        const second = (s2 - i) % n;

        if (dxfData[first].type === "LINE") {
          shapeData.push({
            start: {
              x: (dxfData[first].start.x + dxfData[second].end.x) / 2,
              y: (dxfData[first].start.y + dxfData[second].end.y) / 2,
            },
            end: {
              x: (dxfData[first].end.x + dxfData[second].start.x) / 2,
              y: (dxfData[first].end.y + dxfData[second].start.y) / 2,
            },
            angle: dxfData[first].angle,
            type: "LINE",
          });
        } else if (dxfData[first].type === "ARC") {
          shapeData.push({
            center: { x: dxfData[first].center.x, y: dxfData[first].center.y },
            clockwise: dxfData[first].clockwise,
            startAngle: dxfData[first].startAngle,
            endAngle: dxfData[first].endAngle,
            radius: (dxfData[first].radius + dxfData[second].radius) / 2,
            type: "ARC",
          });
        }
      }
    }

    const safeThickness =
      Number.isFinite(Number(thickness1)) && Number(thickness1) > 0
        ? Number(thickness1)
        : 1;
    setThickness(safeThickness.toFixed(2));
    setInnerThickness((safeThickness / 2).toFixed(2));
    setOuterThickness((safeThickness / 2).toFixed(2));

    let sum = 0;
    for (let i = 0; i < shapeData.length; i++) {
      const s = shapeData[i];
      if (s.type === "ARC") {
        const sweep = arcSweep(s.startAngle, s.endAngle, s.clockwise);
        if (LWPOLYLINE) sum += s.clockwise ? sweep * aa : -sweep * aa;
        else sum += (s.startAngle - s.endAngle) * aa;
      }
    }
    clockwise = sum <= 0;
    setClockWises(clockwise);
    setSelectedShapeData([...shapeData]);
  }, [entities]);

  useEffect(() => {
    setPredefinedPoints([]);
    setThickness(Number(innerThickness) + Number(outerThickness));
    const n = selectedShapeData.length;

    for (let i = 0; i < n; i++) {
      const shape = selectedShapeData[i];
      const newPoint = {
        id: i + 1,
        type: shape.type === "LINE" ? "line" : "circle",
        x:
          shape.type === "LINE"
            ? clockWises
              ? shape.start.x + outerThickness * Math.sin(shape.angle)
              : shape.start.x + innerThickness * Math.sin(shape.angle)
            : shape.center.x,
        y:
          shape.type === "LINE"
            ? clockWises
              ? shape.start.y - outerThickness * Math.cos(shape.angle)
              : shape.start.y - innerThickness * Math.cos(shape.angle)
            : shape.center.y,
        w:
          shape.type === "LINE"
            ? Math.sqrt(
                Math.pow(shape.end.x - shape.start.x, 2) +
                Math.pow(shape.end.y - shape.start.y, 2)
              )
            : 0,
        h: shape.type === "LINE" ? Number(outerThickness) + Number(innerThickness) : 0,
        r:
          shape.type === "LINE"
            ? 0
            : (!shape.clockwise ^ clockWises)
              ? shape.radius + Number(innerThickness)
              : shape.radius + Number(outerThickness),
        angle:
          shape.type === "LINE"
            ? shape.angle * aa
            : aa * arcSweep(shape.startAngle, shape.endAngle, shape.clockwise),
        rotation: shape.type === "LINE" ? 0 : arcRenderRotation(shape),
        t: Number(outerThickness) + Number(innerThickness),
      };
      setPredefinedPoints((prev) => [...prev, newPoint]);
    }
  }, [selectedShapeData, outerThickness, innerThickness, clockWises]);

  useEffect(() => {
    if (predefinedPoints.length === 0 || Number(thickness) <= 0) {
      setWeightPerLength(0);
      setTotalWeight(0);
      setStripWidth(0);
      setOutLine(0);
      setArea(0);
      return;
    }

    ({ a, b } = COM(predefinedPoints));
    ({ Ix, Iy, sw, ol, acs, xmax, ymax, Ixy } = ComputeMomentOfInertia(
      predefinedPoints,
      a,
      b,
      mx,
      ratio,
      thickness
    ));
    const Paa = Math.atan(2 * Ixy / (Ix - Iy)) * 90 / Math.PI;
    const Iu =
      Paa <= 0
        ? (Number(Ix) + Number(Iy)) / 2 -
          Math.sqrt(((Number(Ix) - Number(Iy)) / 2) ** 2 + Ixy * Ixy)
        : (Number(Ix) + Number(Iy)) / 2 +
          Math.sqrt(((Number(Ix) - Number(Iy)) / 2) ** 2 + Ixy * Ixy);
    const Iv =
      Paa > 0
        ? (Number(Ix) + Number(Iy)) / 2 -
          Math.sqrt(((Number(Ix) - Number(Iy)) / 2) ** 2 + Ixy * Ixy)
        : (Number(Ix) + Number(Iy)) / 2 +
          Math.sqrt(((Number(Ix) - Number(Iy)) / 2) ** 2 + Ixy * Ixy);
    const { umax, vmax } = ComputePrincipalAxisAngle(
      predefinedPoints,
      a,
      b,
      mx,
      ratio,
      thickness,
      Paa
    );

    setWeightPerLength((sw * thickness * density * 0.000001).toFixed(3));
    setTotalWeight((sw * thickness * density * 0.000001 * length).toFixed(3));
    setStripWidth(sw.toFixed(3));
    setOutLine(ol.toFixed(3));
    setArea(acs.toFixed(3));
    setInertiax(Ix.toFixed(3));
    setInertiay(Iy.toFixed(3));
    setRogx((Math.sqrt(Ix / acs) * 10).toFixed(3));
    setRogy((Math.sqrt(Iy / acs) * 10).toFixed(3));
    setPmoi((Number(Ix) + Number(Iy)).toFixed(3));
    setMorx((Ix / ymax).toFixed(3));
    setMory((Iy / xmax).toFixed(3));
    if (Ix !== Iy) setPaangle(Paa.toFixed(3));
    setInertiaxy((-Ixy).toFixed(3));
    setInertiau(Iu.toFixed(3));
    setInertiav(Iv.toFixed(3));
    setRogu((Math.sqrt(Iu / acs) * 10).toFixed(3));
    setRogv((Math.sqrt(Iv / acs) * 10).toFixed(3));
    setMoru((Iu / vmax).toFixed(3));
    setMorv((Iv / umax).toFixed(3));
  }, [predefinedPoints, length, density, thickness]);

  useEffect(() => {
    const newWidth = (endX - startX + 40) / scale;
    const newHeight = (endY - startY + 40) / scale;
    const side = Math.max(newWidth, newHeight);
    setViewBox(
      `${(startX + endX) / 2 - side / 2} ${(startY + endY) / 2 - side / 2} ${side} ${side}`
    );
  }, [startX, startY, endX, endY, scale]);

  const handlePan = useCallback((dx, dy) => {
    setViewBox((prev) => {
      const [vx, vy, vw, vh] = prev.split(" ").map(Number);
      return `${vx - dx} ${vy - dy} ${vw} ${vh}`;
    });
  }, []);

  const startDrag = (x, y) => {
    setIsDragging(true);
    setStartCoords({ x, y });
  };

  const handleDrag = (x, y) => {
    if (isDragging) {
      handlePan(x - startCoords.x, y - startCoords.y);
      setStartCoords({ x, y });
    }
  };

  const stopDrag = () => setIsDragging(false);
  const handleMouseDown = (e) => startDrag(e.clientX, e.clientY);
  const handleMouseMove = (e) => handleDrag(e.clientX, e.clientY);
  const handleMouseUp = () => stopDrag();
  const handleTouchStart = (e) => {
    const t = e.touches[0];
    startDrag(t.clientX, t.clientY);
  };
  const handleTouchMove = (e) => {
    const t = e.touches[0];
    handleDrag(t.clientX, t.clientY);
  };
  const handleTouchEnd = () => stopDrag();

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging, startCoords, handlePan]);

  const updateViewBox = () => {
    const newWidth = (endX - startX + 40) / scale;
    const newHeight = (endY - startY + 40) / scale;
    const side = Math.max(newWidth, newHeight);
    setViewBox(
      `${(startX + endX) / 2 - side / 2} ${(startY + endY) / 2 - side / 2} ${side} ${side}`
    );
  };

  const zoomIn = () => setScale((p) => p * 1.2);
  const zoomOut = () => setScale((p) => p / 1.2);
  const resetZoom = () => {
    setScale(1);
    updateViewBox();
  };

  useEffect(() => {
    updateViewBox();
  }, [scale]);

  return (
    <div>
      <div className="modal fade" id="exampleModal0" tabIndex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-body">
              <Feasibility type={type} stripWidth={stripWidth} thickness={thickness} boxPerimeter={boxPerimeter} />
            </div>
          </div>
        </div>
      </div>
      <div className="modal fade" id="exampleModal1" tabIndex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-body">
              <FeasibilityL1 type={type} stripWidth={stripWidth} thickness={thickness} boxPerimeter={boxPerimeter} length={length} />
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", position: "relative" }}>
        <h1 className="heading">DXF Viewer</h1>
        <div className="btn-group" role="group" style={{ marginLeft: "auto", transform: "translateX(-35%)" }}>
          <button
            type="button"
            className="btn btn dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            style={{ marginInline: "10px", color: "white", backgroundColor: "#1b065c", borderRadius: "5px" }}
          >
            Feasibility?
          </button>
          <ul className="dropdown-menu">
            <li><button type="button" className="btn btn" data-bs-toggle="modal" data-bs-target="#exampleModal0">Feasibility L0</button></li>
            <li><button type="button" className="btn btn" data-bs-toggle="modal" data-bs-target="#exampleModal1">Feasibility L1</button></li>
          </ul>
        </div>
      </div>

      <div className="container">
        <div className="box">
          <div style={{ color: "white", backgroundColor: "#1b065c", fontWeight: "bold" }}>Input</div>
          <div className="container1">
            <td><input type="file" accept=".dxf" onChange={handleUpload} /></td>
          </div>
          <div className="container1">
            <label htmlFor="width">Thickness: {thickness} mm</label>
            <select
              value={thicknessIncreaseSide}
              onChange={(e) => setThicknessIncreaseSide(e.target.value)}
              style={{ padding: "8px", border: "1px solid #ccc", borderRight: "none", borderRadius: "6px 0 0 6px", outline: "none" }}
            >
              <option value="Inner">Inner Thickness Increas</option>
              <option value="Outer">Outer Thickness Increas</option>
            </select>
          </div>
          <div className="container1">
            <label className="label" htmlFor="width">Thickness (t) mm</label>
            <input
              type="number"
              value={thicknessIncreaseSide === "Inner" ? innerThickness : outerThickness}
              onChange={handleThicknessChange}
              style={{ padding: "8px", border: "1px solid #ccc", borderRight: "none", borderRadius: "6px 0 0 6px", outline: "none" }}
              onFocus={(e) => e.target.select()}
            />
          </div>
          <CommonInput density={density} setDensity={setDensity} mat={materials} length={length} setLength={setLength} />
        </div>

        <div className="box">
          <div className="form-check form-switch m-0">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="dimensionSwitch"
              title="Click to check dimensions"
              checked={dimensioning}
              onChange={clickOndimensioning}
            />
            <label className="form-check-label ms-2" htmlFor="dimensionSwitch">DIMENSIONING FUNCTION</label>
          </div>

          <svg
            viewBox={viewBox}
            style={{ width: "100%", height: "58vh", backgroundColor: "#f9f9f9", border: "1px solid #ccc" }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          >
            {/* ── Dynamic grid: only rendered when showGrid is true ── */}
            {showGrid && (
              <g>
                <defs>
                  <pattern
                    id="dynamicGrid"
                    width={gridSize}
                    height={gridSize}
                    patternUnits="userSpaceOnUse"
                  >
                    <path
                      d={`M ${gridSize} 0 L 0 0 0 ${gridSize}`}
                      fill="none"
                      stroke="#b0b0b0"
                      strokeWidth={gridSize * 0.015}
                    />
                  </pattern>
                </defs>
                <rect x="-100000" y="-100000" width="200000" height="200000" fill="url(#dynamicGrid)" />
              </g>
            )}

            {selectedShapeData.map((shape, i) => (
              <g key={i}>
                {shape.type === "LINE" && (
                  <LineAtTheta
                    x={
                      clockWises
                        ? shape.start.x + outerThickness * Math.sin(shape.angle)
                        : shape.start.x + innerThickness * Math.sin(shape.angle)
                    }
                    y={
                      clockWises
                        ? shape.start.y - outerThickness * Math.cos(shape.angle)
                        : shape.start.y - innerThickness * Math.cos(shape.angle)
                    }
                    w={Math.sqrt(
                      Math.pow(shape.end.x - shape.start.x, 2) +
                      Math.pow(shape.end.y - shape.start.y, 2)
                    )}
                    h={Number(outerThickness) + Number(innerThickness)}
                    angle={shape.angle * aa}
                  />
                )}
                {shape.type === "ARC" && (
                  <CircleSector
                    radius={
                      (!shape.clockwise ^ clockWises)
                        ? shape.radius + Number(innerThickness)
                        : shape.radius + Number(outerThickness)
                    }
                    centerX={shape.center.x}
                    centerY={shape.center.y}
                    angle={aa * arcSweep(shape.startAngle, shape.endAngle, shape.clockwise)}
                    rotation={arcRenderRotation(shape)}
                    thickness={Number(outerThickness) + Number(innerThickness)}
                  />
                )}
              </g>
            ))}

            {dimensioning && (
              <PredefinedPoints points={predefinedPoints} mx={100} thickness={thickness} scale={100} />
            )}
          </svg>

          {/* ── Zoom controls + grid toggle ── */}
          <div style={{ display: "flex", justifyContent: "center" }}>
            <button
              title="Zoom in"
              className="btn btn mx-2 my-2"
              onClick={zoomIn}
              style={{ color: "white", backgroundColor: "#1b065c" }}
            >
              <i className="fa-solid fa-magnifying-glass-plus"></i>
            </button>
            <button
              title="Reset zoom"
              className="btn btn mx-2 my-2"
              onClick={resetZoom}
              style={{ color: "white", backgroundColor: "#1b065c" }}
            >
              <i className="fa-solid fa-maximize"></i>
            </button>
            <button
              title="Zoom out"
              className="btn btn mx-2 my-2"
              onClick={zoomOut}
              style={{ color: "white", backgroundColor: "#1b065c" }}
            >
              <i className="fa-solid fa-magnifying-glass-minus"></i>
            </button>
            {/* ── Grid toggle button ── */}
            <button
              title={showGrid ? "Hide grid" : "Show grid"}
              className="btn btn mx-2 my-2"
              onClick={() => setShowGrid((prev) => !prev)}
              style={{
                color: "white",
                backgroundColor: showGrid ? "#1b065c" : "#6c757d",
                minWidth: "80px",
              }}
            >
              <i className="fa-solid fa-border-all"></i>
              &nbsp;{showGrid ? "Grid ON" : "Grid OFF"}
            </button>
          </div>
        </div>

        <div className="box">
          <Result
            weightPerLength={weightPerLength}
            length={length}
            totalWeight={totalWeight}
            stripWidth={stripWidth}
            outLine={outLine}
            area={area}
            inertiax={inertiax}
            inertiay={inertiay}
            rogx={rogx}
            rogy={rogy}
            pmoi={pmoi}
            morx={morx}
            mory={mory}
            inertiaxy={inertiaxy}
            paangle={paangle}
            inertiau={inertiau}
            inertiav={inertiav}
            rogu={rogu}
            rogv={rogv}
            moru={moru}
            morv={morv}
          />
        </div>
      </div>
    </div>
  );
}
